import { Col, Row } from '@advanza/grid'
import Modal from '@advanza/modal'
import { Button, Divider, Icon, PreIcon } from '@advanza/ui'
import A from 'components/ui/A'
import { useAppSelector } from 'hooks/hooks'
import { useCopyInvalidMatch } from 'hooks/matchesHooks'
import { useState } from 'react'

function useMatchDelayReason(currentMatch) {
    const { matches } = useAppSelector((state) => state.matches.entities)
    const now = new Date()
    const isDelayed = !currentMatch.confirmed && currentMatch.freeLeadDelay
    const hasActivePremiumExlusive =
        matches &&
        Object.keys(matches)
            .map((matchId) => matches[matchId])
            .filter((match) => match.request_id === currentMatch.request_id)
            .findIndex(
                (match) =>
                    match.confirmed &&
                    (match.isSigned || match.isFreeTrialLead) &&
                    match.exclusive_label &&
                    !match.exclusive_expired,
            ) !== -1

    // Get country-specific configuration
    const countryCode = currentMatch.country_code || 'NL'
    const countryConfig = getCountryConfig(countryCode)

    // Check if we're in night period for this country
    const isNightPeriod = isWithinNightPeriod(now, countryCode)
    const nightlyExclusiveDelay = isDelayed && isNightPeriod && hasActivePremiumExlusive

    let delayReason
    if (nightlyExclusiveDelay) {
        // Calculate the end time for the delay
        const delayEndTime = calculateDelayEndTime(currentMatch, countryConfig)
        delayReason = `This match is delayed until ${formatTime(
            delayEndTime,
        )}, to prevent an exclusive premium match from expiring at night.`
    } else {
        delayReason = `This match is ${
            countryConfig.freeLeadDelay || 8
        } hours delayed, because it's not a premium match.`
    }

    return {
        isDelayed,
        delayReason,
    }
}

// Helper function to get country configuration
function getCountryConfig(countryCode) {
    const configs = {
        NL: {
            timezone: 'Europe/Amsterdam',
            freeLeadDelay: 8,
            businessHours: { start: 8, end: 20 },
            nightPeriod: { start: 20, end: 8 },
        },
        DE: {
            timezone: 'Europe/Berlin',
            freeLeadDelay: 8,
            businessHours: { start: 8, end: 20 },
            nightPeriod: { start: 20, end: 8 },
        },
        BE: {
            timezone: 'Europe/Brussels',
            freeLeadDelay: 8,
            businessHours: { start: 8, end: 20 },
            nightPeriod: { start: 20, end: 8 },
        },
        ES: {
            timezone: 'Europe/Madrid',
            freeLeadDelay: 8,
            businessHours: { start: 8, end: 20 },
            nightPeriod: { start: 20, end: 8 },
        },
    }
    return configs[countryCode] || configs['NL']
}

// Helper function to check if time is within night period for a country
function isWithinNightPeriod(date, countryCode) {
    const config = getCountryConfig(countryCode)
    const hour = date.getUTCHours()
    return hour >= config.nightPeriod.start || hour < config.nightPeriod.end
}

// Helper function to calculate delay end time
function calculateDelayEndTime(match, countryConfig) {
    // This would need to be implemented based on the backend logic
    // For now, return a placeholder
    const now = new Date()
    const delayEnd = new Date(now.getTime() + countryConfig.freeLeadDelay * 60 * 60 * 1000)
    return delayEnd
}

// Helper function to format time for display
function formatTime(date) {
    return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
    })
}

/**
 * @param {{ match: any, renderTrigger?: (args: { open: () => void }) => import('react').ReactNode }} props
 */
const ManualConfirmButton = ({ match, renderTrigger = null }) => {
    const [open, setOpen] = useState(false)
    const {
        error,
        copyMatchAndConfirm,
        isPosting,
        result,
        copyRequestDetails,
        isCopying,
        isCopied,
    } = useCopyInvalidMatch(match.match_id)
    const { isDelayed, delayReason } = useMatchDelayReason(match)
    const trigger = renderTrigger
        ? renderTrigger({ open: () => setOpen(true) })
        : !match.confirmed && (
              <button onClick={() => setOpen(true)}>
                  <Icon
                      name={
                          isDelayed
                              ? 'alarm_on'
                              : match.processingNewRequest
                                ? 'rotate_right'
                                : 'error'
                      }
                      orange
                  />
              </button>
          )

    return (
        <>
            {trigger}
            <Modal open={open} close={() => setOpen(false)} maxWidth={800}>
                <h3>Match #{match.match_id} is not confirmed</h3>
                <Divider m />
                {isDelayed ? (
                    <div style={{ color: '#f46511' }}>
                        <PreIcon name="alarm_on" orange>
                            {delayReason}
                        </PreIcon>
                        <Divider l />
                    </div>
                ) : match.processingNewRequest ? (
                    <div style={{ color: '#f46511' }}>
                        <PreIcon icon="rotate_right" orange>
                            The request of this match is still being processing. Please wait a few
                            minutes...
                        </PreIcon>
                        <Divider l />
                    </div>
                ) : null}
                {error && <div style={{ color: '#f00' }}>Error: {error.error}</div>}
                {result && result.status === 'success' && (
                    <div style={{ color: '#1aa225' }}>
                        <div>
                            Added to request:{' '}
                            <A to={`/service/requests/request/${result.requestId}`}>
                                #{result.requestId}
                            </A>
                        </div>
                        <Divider m />
                        <div>
                            New matchId:{' '}
                            <A to={`/service/matches/match/${result.matchId}`}>#{result.matchId}</A>
                        </div>
                    </div>
                )}
                {isCopied && isCopied.status === 'success' && (
                    <div style={{ color: '#1aa225' }}>
                        <div>
                            Added request details & images as message to matchId:{' '}
                            <A to={`/service/matches/match/${isCopied.matchId}`}>
                                <u>#{isCopied.matchId}</u>
                            </A>
                        </div>
                    </div>
                )}
                {!result && !isCopied && (
                    <div>
                        If the match belongs to an invalid request, you can try to:
                        <ul>
                            <li>
                                - <em>copy the match and confirm</em> if the request has a valid
                                duplicate
                            </li>
                            <li>
                                - <em>copy the request details only</em> if the provider has already
                                a charged match from the same user
                            </li>
                        </ul>
                    </div>
                )}
                <Divider l />
                <Row middle="xs" end="xs">
                    {result || isCopied ? (
                        <>
                            <Col x>
                                <Button name="primary" onClick={() => setOpen(false)}>
                                    Close
                                </Button>
                            </Col>
                        </>
                    ) : (
                        <>
                            <Col x>
                                <Button name="bordered" onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                            </Col>
                            <Col x>
                                <Button
                                    name="primary"
                                    disabled={isPosting}
                                    onClick={copyMatchAndConfirm}>
                                    <PreIcon name="content_copy" white>
                                        {isPosting ? `bezig...` : `Copy match and confirm`}
                                    </PreIcon>
                                </Button>
                            </Col>
                            <Col x>
                                <Button
                                    name="primary"
                                    disabled={isCopying}
                                    onClick={copyRequestDetails}>
                                    <PreIcon name="content_copy" white>
                                        {isCopying
                                            ? `bezig...`
                                            : `Copy request details & images only`}
                                    </PreIcon>
                                </Button>
                            </Col>
                        </>
                    )}
                </Row>
            </Modal>
        </>
    )
}

export default ManualConfirmButton
